import { isValidMobileNumber } from '@psyomics/ui-common';
import { Header, Input, Label, PhoneInput } from '@psyomics/ui-components';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RespondentType } from './generated/referral-request';

type RespondentFormValue = { givenName: string; familyName: string; phoneNumber: string };

const emptyRespondentFormValue: RespondentFormValue = { givenName: '', familyName: '', phoneNumber: '' };

type FormValues = {
  patientId: string;
  respondents: Partial<Record<RespondentType, RespondentFormValue>>;
};

const getRespondentFormValue = (respondent?: RespondentFormValue): RespondentFormValue =>
  respondent ?? emptyRespondentFormValue;

const hasRespondentName = ({ familyName, givenName }: RespondentFormValue) =>
  givenName.trim().length > 0 || familyName.trim().length > 0;

const hasRespondentValue = (respondent: RespondentFormValue) =>
  hasRespondentName(respondent) || respondent.phoneNumber.trim().length > 0;

const isPatientRespondent = (respondentType: RespondentType) => respondentType === RespondentType.Patient;

const isRespondentFieldRequired = (respondent: RespondentFormValue, respondentType: RespondentType) =>
  isPatientRespondent(respondentType) || hasRespondentValue(respondent);

type RespondentPhoneFieldProps = {
  readonly control: ReturnType<typeof useForm<FormValues>>['control'];
  readonly disabled?: boolean;
  readonly errorRequired: string;
  readonly getValues: ReturnType<typeof useForm<FormValues>>['getValues'];
  readonly respondentType: RespondentType;
  readonly invalidMessage: string;
  readonly hasError: boolean;
};

type RespondentNameFieldsProps = {
  readonly disabled?: boolean;
  readonly errorRequired: string;
  readonly familyNameErrorMessage?: string;
  readonly givenNameErrorMessage?: string;
  readonly getValues: ReturnType<typeof useForm<FormValues>>['getValues'];
  readonly register: ReturnType<typeof useForm<FormValues>>['register'];
  readonly respondentType: RespondentType;
};

const RespondentNameFields = ({
  disabled,
  errorRequired,
  familyNameErrorMessage,
  givenNameErrorMessage,
  getValues,
  register,
  respondentType,
}: RespondentNameFieldsProps) => {
  const { t } = useTranslation();

  const validateNameField = (value: string) => {
    const respondent = getRespondentFormValue(getValues(`respondents.${respondentType}`));
    if (!value.trim().length && isRespondentFieldRequired(respondent, respondentType)) return errorRequired;
    return true;
  };

  return (
    <>
      <Label htmlFor={`respondents.${respondentType}.givenName`}>
        {t('page.professionalReferral.referralRequest.givenNameLabel')}
      </Label>
      <Input
        disabled={disabled}
        id={`respondents.${respondentType}.givenName`}
        variant={givenNameErrorMessage ? 'error' : 'default'}
        {...register(`respondents.${respondentType}.givenName`, { validate: validateNameField })}
      />
      <Label htmlFor={`respondents.${respondentType}.familyName`}>
        {t('page.professionalReferral.referralRequest.familyNameLabel')}
      </Label>
      <Input
        disabled={disabled}
        id={`respondents.${respondentType}.familyName`}
        variant={familyNameErrorMessage ? 'error' : 'default'}
        {...register(`respondents.${respondentType}.familyName`, { validate: validateNameField })}
      />
    </>
  );
};

const RespondentPhoneField = ({
  control,
  disabled,
  respondentType,
  getValues,
  errorRequired,
  invalidMessage,
  hasError,
}: RespondentPhoneFieldProps) => (
  <Controller
    control={control}
    name={`respondents.${respondentType}.phoneNumber`}
    render={({ field: { name, onBlur, onChange, value } }) => (
      <PhoneInput
        disabled={disabled}
        name={name}
        onBlur={onBlur}
        onChangeNumber={onChange}
        value={value}
        variant={hasError ? 'error' : 'default'}
      />
    )}
    rules={{
      validate: (value: string) => {
        const respondent = getRespondentFormValue(getValues(`respondents.${respondentType}`));
        if (!value?.trim().length && !isPatientRespondent(respondentType) && hasRespondentName(respondent)) {
          return errorRequired;
        }
        if (value?.trim().length && !isValidMobileNumber(value)) return invalidMessage;
        return true;
      },
    }}
  />
);

const RespondentFields = ({
  control,
  disabled,
  getValues,
  register,
  respondentType,
  errorRequired,
  familyNameErrorMessage,
  givenNameErrorMessage,
  invalidMessage,
  phoneErrorMessage,
}: {
  readonly control: ReturnType<typeof useForm<FormValues>>['control'];
  readonly disabled?: boolean;
  readonly getValues: ReturnType<typeof useForm<FormValues>>['getValues'];
  readonly register: ReturnType<typeof useForm<FormValues>>['register'];
  readonly respondentType: RespondentType;
  readonly errorRequired: string;
  readonly familyNameErrorMessage?: string;
  readonly givenNameErrorMessage?: string;
  readonly invalidMessage: string;
  readonly phoneErrorMessage?: string;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 mt-4">
      <Header level="h4">{t(`model.common.respondentType.${respondentType}`)}</Header>
      <RespondentNameFields
        disabled={disabled}
        errorRequired={errorRequired}
        familyNameErrorMessage={familyNameErrorMessage}
        getValues={getValues}
        givenNameErrorMessage={givenNameErrorMessage}
        register={register}
        respondentType={respondentType}
      />
      <Label htmlFor={`respondents.${respondentType}.phoneNumber`}>
        {t('page.professionalReferral.referralRequest.phoneNumberLabel')}
      </Label>
      <RespondentPhoneField
        control={control}
        disabled={disabled}
        errorRequired={errorRequired}
        getValues={getValues}
        hasError={Boolean(phoneErrorMessage)}
        invalidMessage={invalidMessage}
        respondentType={respondentType}
      />
    </div>
  );
};

export {
  type FormValues,
  getRespondentFormValue,
  isRespondentFieldRequired,
  RespondentFields,
  type RespondentFormValue,
};
